import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MediaPipelineInfo, MediaProcessInfo, standardGetHttpOptions, standardHttpOptions } from '@models';
import { LoadingService } from '@services/loading.service';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import {  catchError, finalize, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { ScoutviewImage, ScoutviewMetadataSearchCriteria, ScoutviewExpiringSearchCriteria, ScoutviewSourceLoadCriteria } from '@models/Scoutview/scoutview.model';
import { GatewayImageStatsInfo, SvMetadataInfo } from '@models/Scoutview/scoutviewInfo.model';


@Injectable()
export class ScoutviewService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
              private loadingService: LoadingService,
              @Inject(KIG_SETTINGS) private settings: KigSettings,
              private userService: UserService) {
    this.apiUrl = this.settings.apiUrl;
  }

  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  public getSvImageAsBase64(sourceId: string, fileName: string, loadMessage: string): Observable<any> {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.get(`${ this.apiUrl }/scoutview/ImageAsBase64/${sourceId}/${fileName}`, {responseType:'text'}).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public getSvImage(sourceId: string, fileName: string, loadMessage: string): Observable<any> {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.get(`${ this.apiUrl }/scoutview/Image/${sourceId}/${fileName}`, {responseType:'blob'}).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public getListOfSvImagesForIdentifier(sourceId: string, kind: string): Observable<string[]> {
    this.loadingService.setMessage('Loading image list ... ...');
    return this.http.get<string[]>(`${ this.apiUrl }/scoutview/ImageList/${sourceId}/${kind}`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSvMetaDataCount(): Observable<number> {
    this.loadingService.setMessage('Loading image list ... ...');
    return this.http.get<number>(`${ this.apiUrl }/scoutview/Metadata/count`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSvMetadatasForSourceIds(sourceIds: string[]): Observable<SvMetadataInfo[]> {
    this.loadingService.setMessage('Getting metadata for given identifier/source ...');
    return this.http.post<SvMetadataInfo[]>(`${ this.apiUrl }/scoutview/Metadata/SourceIds`, JSON.stringify(sourceIds), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSvMetadatasWithSearchParameters(searchCriteria: ScoutviewMetadataSearchCriteria): Observable<SvMetadataInfo[]> {
    this.loadingService.setMessage('Retrieving image metadata ...');
    return this.http.post<SvMetadataInfo[]>(`${ this.apiUrl }/scoutview/Metadata/Search`, JSON.stringify(searchCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getGatewayImageStats(searchCriteria: ScoutviewMetadataSearchCriteria): Observable<GatewayImageStatsInfo[]> {
    // Silent (no loading spinner) — fires on load and on date-range change alongside the alive poll.
    return this.http.post<GatewayImageStatsInfo[]>(`${ this.apiUrl }/scoutview/Metadata/expireStats`, JSON.stringify(searchCriteria), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getExpiringSvMetadatas(expiringSearchCriteria: ScoutviewExpiringSearchCriteria): Observable<SvMetadataInfo[]> {
    this.loadingService.setMessage('Retrieving expiring images ...');
    return this.http.post<SvMetadataInfo[]>(`${ this.apiUrl }/scoutview/Metadata/expiring`, JSON.stringify(expiringSearchCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSvMetadatasForSource(sourceLoadCriteria: ScoutviewSourceLoadCriteria): Observable<SvMetadataInfo[]> {
    this.loadingService.setMessage('Retrieving images ...');
    return this.http.post<SvMetadataInfo[]>(`${ this.apiUrl }/scoutview/Metadata/bySource`, JSON.stringify(sourceLoadCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSvMetadataWithTags(tags: string[]): Observable<SvMetadataInfo[]> {
    this.loadingService.setMessage('Getting metadata matching tags ...');
    return this.http.post<SvMetadataInfo[]>(`${ this.apiUrl }/scoutview/Metadata/withTags`, JSON.stringify(tags), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  
  public getAllSvMetadata(): Observable<SvMetadataInfo[]> {
    this.loadingService.setMessage('Getting all metadata ...');
    return this.http.get<SvMetadataInfo[]>(`${ this.apiUrl }/scoutview/Metadata/all`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  deleteSvImagesAndMetadata(sourceId: string, fileName: string): Observable<any> {
    return this.http.delete(`${ this.apiUrl }/scoutview/${ sourceId }/${ fileName }`, this.standardHttpOptionsWithUser).pipe(
      catchError(this.handleError)
    );
  }


  updateSvMetadata(svMetaData: SvMetadataInfo): Observable<any> {
    return this.http.put(`${ this.apiUrl }/scoutview/metadata/`, JSON.stringify(svMetaData), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public uploadImage(file: File, renamedFileName: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, renamedFileName);
    this.loadingService.setMessage('Uploading image...');
    return this.http.post(`${ this.apiUrl }/scoutview/uploadimage`, formData, { withCredentials: true }).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
